import React from "react"
import { cn } from "design_system/libs"
import { twMerge } from "tailwind-merge"

// A song stands on the shelf as a record: either a vinyl DISC (a grooved circle with a coloured centre
// label) or a SLEEVE (an album-cover square with the disc peeking out). Which one — and the label/sleeve
// colour — is chosen deterministically from the item id, so a given song always looks the same.

export interface SongVinylItem {
  id?: string | null
  name?: string | null
  path?: string | null
  imageUrl?: string | null
  thumbnailUrl?: string | null
}

export interface SongVinylTheme {
  /** The vinyl body (near-black wax). */
  disc: string
  /** The centre label + sleeve accent — the pop of colour, picked from the palette by seed. */
  labels: string[]
  /** Groove sheen colour. */
  groove: string
  /** Tailwind bg utility for the (image-less) sleeve — the element's brand token, e.g. `bg-air`, so the
   *  sleeve reads as the element's default colour rather than a seeded label. Empty -> seeded label hex. */
  sleeveClass?: string
}

// Vinyl themes come from the shared element registry (element_themes.ts), keyed by element so a record's
// disc + label colours match the rest of the shelf. A caller can still pass a custom SongVinylTheme.
// Extend by adding an element in element_themes.ts.
import { VINYL_ELEMENT_THEMES, type ElementThemeName } from "./element_themes"

export const SONG_VINYL_THEMES = VINYL_ELEMENT_THEMES
export type SongVinylThemeName = ElementThemeName
export const DEFAULT_SONG_VINYL_THEME: SongVinylTheme = VINYL_ELEMENT_THEMES.default

// Stable 32-bit hash so the variant + colour are content-addressed by id (fallback to name).
const seedFrom = (value: string): number => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }
  return hash
}

const resolveTheme = (
  theme?: SongVinylThemeName | SongVinylTheme
): SongVinylTheme =>
  typeof theme === "string"
    ? SONG_VINYL_THEMES[theme] ?? DEFAULT_SONG_VINYL_THEME
    : theme ?? DEFAULT_SONG_VINYL_THEME

// Curved text can't overflow-ellipsis like CSS, so cap the title to what fits the label's top arc.
const MAX_TITLE = 16
const truncate = (name: string) =>
  name.length > MAX_TITLE ? `${name.slice(0, MAX_TITLE - 1).trimEnd()}…` : name

// The vinyl disc: concentric grooves (repeating radial gradient) around a coloured centre label, with the
// (truncated) song title curved around the top of that label.
const Disc: React.FC<{
  label: string
  groove: string
  disc: string
  name: string
  /** When present (show-image enabled), the album art fills the disc behind the grooves, tinted by the theme
   *  colour, with the grooves ("radar") rendered translucently over it. Absent -> the generated flat disc. */
  imageUrl?: string | null
  className?: string
}> = ({ label, groove, disc, name, imageUrl, className }) => {
  // A unique path id per instance — several discs on a shelf must not all resolve `href` to the first path.
  const arcId = React.useId()

  // Concentric groove rings ("radar"): opaque disc-coloured over the flat disc; translucent over art so the
  // image shows between the grooves.
  const grooves = imageUrl
    ? `repeating-radial-gradient(circle at 50% 50%, rgba(0,0,0,0.5) 0 2px, ${groove} 2px 3px)`
    : `radial-gradient(circle at 50% 50%, ${disc} 0 28%, transparent 28%), repeating-radial-gradient(circle at 50% 50%, ${disc} 0 2px, ${groove} 2px 3px)`

  return (
    <div
      data-testid="song-vinyl-disc"
      className={twMerge(
        "relative flex h-[168px] w-[168px] items-center justify-center overflow-hidden rounded-full shadow-md",
        className
      )}
      style={
        imageUrl
          ? undefined
          : {
              background: grooves,
              backgroundColor: `oklch(from ${label} calc(l - 0.2) c h)`,
            }
      }
    >
      {imageUrl && (
        <>
          {/* Album art behind the radar */}
          <img
            src={imageUrl}
            alt={name}
            className="absolute inset-0 h-full w-full rounded-full object-cover"
          />
          {/* Theme colour as a filter over the art (multiply keeps detail while shifting it to the hue) */}
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{ background: label, mixBlendMode: "multiply", opacity: 0.55 }}
          />
          {/* The grooves ("radar") layered over the tinted art */}
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{ background: grooves, opacity: 0.6 }}
          />
        </>
      )}
      {/* centre label: the title rides the top arc, curving around the centre */}
      <div
        className="relative flex aspect-square w-[64px] items-center justify-center rounded-full text-white/95"
        style={{ background: label }}
      >
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 h-full w-full overflow-visible"
        >
          <defs>
            {/* Full circle drawn clockwise from the 9-o'clock point; at 25% along it we're at the top,
                so the title reads left-to-right, right-side up, curving over the centre. */}
            <path
              id={arcId}
              fill="none"
              d="M 50,50 m -30,0 a 30,30 0 1,1 60,0 a 30,30 0 1,1 -60,0"
            />
          </defs>
          <text
            fill="currentColor"
            fontSize="11"
            fontWeight="800"
            letterSpacing="0.2"
          >
            <textPath href={`#${arcId}`} startOffset="25%" textAnchor="middle">
              {truncate(name)}
            </textPath>
          </text>
        </svg>
      </div>
      {/* spindle hole */}
      <div className="absolute h-2 w-2 rounded-full bg-black ring-1 ring-white/30" />
    </div>
  )
}

// The sleeve: a square cover (art if present, else a coloured card with the disc edge peeking out).
const Sleeve: React.FC<{
  label: string
  disc: string
  name: string
  imageUrl?: string | null
  /** Tailwind bg utility for the image-less sleeve (the element's brand token). Falls back to `label`. */
  sleeveClass?: string
  children?: React.ReactNode
}> = ({ label, disc, name, imageUrl, sleeveClass, children }) => (
  <div
    data-testid="song-vinyl-sleeve"
    className="group relative h-[168px] w-[168px] shrink-0 transition-transform duration-300 ease-out motion-safe:hover:-translate-y-2 motion-safe:hover:-rotate-6 origin-bottom-left"
  >
    {children}
    {/* the cover: album art if present; else the element's brand colour (bg token) or the seeded label. */}
    <div
      className={cn(
        "absolute inset-0 overflow-hidden rounded-sm border border-black/40 shadow-md",
        !imageUrl && sleeveClass
      )}
      style={{ background: imageUrl || sleeveClass ? undefined : label }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-end p-2">
          <span className="line-clamp-3 text-xl text-white/95 font-fancy">
            {name}
          </span>
        </div>
      )}
    </div>
  </div>
)

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

// Wraps the disc and spins it via the Web Animations API. A CSS keyframe spin can't ease back when the
// hover ends (it snaps to 0, flicking the label sideways); here we read the angle it stopped at and ease it
// back to upright so the record slows to a rest with its label readable. Reduced-motion users get no spin.
const SpinningDisc: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const spin = React.useRef<Animation | null>(null)

  const start = () => {
    const el = ref.current
    if (!el || prefersReducedMotion()) return
    spin.current?.cancel()
    spin.current = el.animate(
      [{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }],
      { duration: 4000, iterations: Infinity, easing: "linear" }
    )
  }

  const stop = () => {
    const el = ref.current
    if (!el || !spin.current) return
    spin.current.cancel()
    spin.current = null
    const transform = getComputedStyle(el).transform
    if (!transform || transform === "none" || typeof DOMMatrixReadOnly === "undefined")
      return
    // Angle the disc stopped at, eased back to upright (0deg) so the curved label reads right at rest.
    const m = new DOMMatrixReadOnly(transform)
    const angle = (Math.atan2(m.b, m.a) * 180) / Math.PI
    el.animate(
      [{ transform: `rotate(${angle}deg)` }, { transform: "rotate(0deg)" }],
      { duration: 700, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    )
  }

  return (
    <div
      ref={ref}
      onMouseEnter={start}
      onMouseLeave={stop}
      className="will-change-transform"
    >
      {children}
    </div>
  )
}

export interface SongVinylProps {
  item: SongVinylItem
  /** Force a variant; by default it's chosen from the id seed. */
  variant?: "disc" | "sleeve"
  theme?: SongVinylThemeName | SongVinylTheme
  className?: string
}

/**
 * Render a song as a record on the shelf — a grooved vinyl disc or an album sleeve, chosen deterministically
 * from the item id (so it's stable across renders), with the label/sleeve colour seeded the same way.
 */
export const SongVinyl: React.FC<SongVinylProps> = ({
  item,
  variant,
  theme,
  className,
}) => {
  const name = item.name || "Untitled"
  const seed = seedFrom(item.id || item.name || "song")
  const resolved = resolveTheme(theme)
  const label = resolved.labels[seed % resolved.labels.length]
  const chosen = variant ?? (seed % 2 === 0 ? "disc" : "sleeve")
  const image = item.imageUrl || item.thumbnailUrl || undefined

  const inner =
    chosen === "disc" ? (
      <div className="group transition-transform duration-300 ease-out motion-safe:hover:-translate-y-2">
        <SpinningDisc>
          <Disc
            label={label}
            groove={resolved.groove}
            disc={resolved.disc}
            name={name}
            // Album art behind the grooves when show-image is enabled (renderShelfItem strips it when off).
            imageUrl={image}
          />
        </SpinningDisc>
      </div>
    ) : (
      <Sleeve
        label={label}
        disc={resolved.disc}
        name={name}
        imageUrl={image}
        sleeveClass={resolved.sleeveClass}
      >
        <Disc
          label={label}
          groove={resolved.groove}
          disc={resolved.disc}
          name={name}
          className="absolute right-1 top-1/2 -translate-y-1/2 translate-x-6 rounded-full shadow-inner transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-12"
        />
      </Sleeve>
    )

  const content = <div className={cn("self-end", className)}>{inner}</div>

  return item.path ? (
    // w-fit for parity with the other shelf items (see book_cover) — keeps the slide from collapsing in
    // iOS Safari's basis-auto flex sizing. Equals the fixed w-[168px] here, so it's a no-op on other browsers.
    <a href={item.path} aria-label={name} className="block w-fit">
      {content}
    </a>
  ) : (
    content
  )
}

export default SongVinyl
